import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from '@mui/material';
import { map, trim } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Form } from 'react-final-form';

import { updateInvoiceGroupBillToApi } from 'api/invoiceGroup';
import usStateAbbreviations from 'common/constants/usStateAbbreviation';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import { validateEmail } from 'common/forms/formValidations';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';

// Field rules mirror the Invoicing API's PUT /v1/invoicing-groups/{id}/bill-to so the admin sees
// problems inline; the API still validates and its message is shown if it rejects the save.
const validateTenDigitPhone = (value) =>
  !value || (value.match(/\d/g) ?? []).length === 10 ? undefined : 'Phone must be 10 digits';

const validateInvoiceZip = (value) =>
  !value || /^\d{5}(-\d{4})?$/.test(trim(value)) ? undefined : 'Zip must be 12345 or 12345-6789';

const toFormValues = (billTo) => ({
  firstName: billTo?.firstName ?? '',
  lastName: billTo?.lastName ?? '',
  phone: billTo?.phone ?? '',
  email: billTo?.email ?? '',
  streetAddress: billTo?.streetAddress ?? '',
  streetAddress2: billTo?.streetAddress2 ?? '',
  city: billTo?.city ?? '',
  // The list can hand back a lowercase or blank state; the select only matches uppercase codes.
  state: (billTo?.state ?? '').toUpperCase(),
  zip: billTo?.zip ?? '',
});

const toBillToInput = (values) => ({
  firstName: trim(values.firstName),
  lastName: trim(values.lastName),
  phone: trim(values.phone),
  email: trim(values.email),
  streetAddress: trim(values.streetAddress),
  streetAddress2: trim(values.streetAddress2) || null,
  city: trim(values.city),
  state: values.state,
  zip: trim(values.zip),
});

const EditInvoiceGroupContactDialog = ({ open, onClose, onSaved, invoiceGroupId, billTo }) => {
  const [isSaving, setIsSaving] = useState(false);
  // Snapshot on open so a parent re-render mid-edit doesn't reset what the admin typed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(() => toFormValues(billTo), [open]);

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSubmit = async (values) => {
    setIsSaving(true);

    try {
      const updatedGroup = await updateInvoiceGroupBillToApi({
        invoiceGroupId,
        billTo: toBillToInput(values),
      });
      enqueueSnackbar('Invoice group contact updated', { variant: 'success' });
      onSaved(updatedGroup);
    } catch (error) {
      enqueueSnackbar(getGraphQLErrorMessage(error), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='EditInvoiceGroupContact-header'
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle id='EditInvoiceGroupContact-header'>Edit Contact</DialogTitle>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit: onSubmit, invalid, pristine }) => (
          <form noValidate onSubmit={onSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name='firstName'
                    id='EditInvoiceGroupContact-firstName'
                    label='First Name'
                    maxLength={100}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name='lastName'
                    id='EditInvoiceGroupContact-lastName'
                    label='Last Name'
                    maxLength={100}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name='email'
                    id='EditInvoiceGroupContact-email'
                    label='Email'
                    maxLength={254}
                    required
                    validations={[validateEmail()]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name='phone'
                    id='EditInvoiceGroupContact-phone'
                    label='Phone'
                    maxLength={20}
                    required
                    validations={[validateTenDigitPhone]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name='streetAddress'
                    id='EditInvoiceGroupContact-streetAddress'
                    label='Address Line 1'
                    maxLength={200}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name='streetAddress2'
                    id='EditInvoiceGroupContact-streetAddress2'
                    label='Address Line 2'
                    maxLength={200}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    name='city'
                    id='EditInvoiceGroupContact-city'
                    label='City'
                    maxLength={100}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SelectField
                    name='state'
                    id='EditInvoiceGroupContact-state'
                    label='State'
                    required
                  >
                    {map(usStateAbbreviations, ({ name, abbreviation }) => (
                      <MenuItem key={abbreviation} value={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    name='zip'
                    id='EditInvoiceGroupContact-zip'
                    label='Zip Code'
                    maxLength={10}
                    required
                    validations={[validateInvoiceZip]}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={isSaving}
                variant='contained'
                type='submit'
                disabled={invalid || pristine}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

EditInvoiceGroupContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  invoiceGroupId: PropTypes.string.isRequired,
  billTo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    streetAddress: PropTypes.string,
    streetAddress2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
};

EditInvoiceGroupContactDialog.defaultProps = {
  billTo: undefined,
};

export default EditInvoiceGroupContactDialog;
